import React, {useMemo} from "react";
import {Table, TableBody, TableCell, TableRow, Typography} from "@mui/material";
import {UserField} from "@atttomyx/react-components";
import {ConnectionField} from "../fields";
import {arrays, objects, strings, users} from "@atttomyx/shared-utils";
import {ADD_NEW} from "../../constants";
import "./connectionForm.css";

const ConnectionForm = (props) => {
    const {horizontal, categories, people, allowNew, locked, data, onChange: onSync} = props;

    const available = useMemo(() => {
        const ret = objects.deepCopy(people);

        if (allowNew) {
            arrays.addTo(ret, {
                id: ADD_NEW,
                imageUrl: null,
                firstName: "New",
                lastName: "Person",
            });
        }

        return ret;
    }, [people, allowNew]);

    const idToName = useMemo(() => {
        const map = {};

        available.forEach(person => {
            map[person.id] = users.getFullName(person);
        });

        return map;
    }, [available]);

    const sources = useMemo(() => {
        return data.targetId ? available.filter(person => person.id !== data.targetId) : available;
    }, [available, data.targetId]);

    const targets = useMemo(() => {
        return data.sourceId ? available.filter(person => person.id !== data.sourceId) : available;
    }, [available, data.sourceId]);

    const source = useMemo(() => {
        return horizontal ? "Left" : "Top";
    }, [data.horizontal]);

    const target = useMemo(() => {
        return horizontal ? "Right" : "Bottom";
    }, [data.horizontal]);

    const description = useMemo(() => {
        const source = objects.defaultIfNullOrUndefined(idToName[data.sourceId], "Somebody");
        const target = objects.defaultIfNullOrUndefined(idToName[data.targetId], "Somebody");
        const connection = objects.defaultIfNullOrUndefined(data.label, "connection");

        return `${target} is ${strings.possessive(source)} ${connection}`;
    }, [idToName, data.sourceId, data.targetId, data.label]);

    const onChange = (field, value) => {
        const modified = objects.deepCopy(data);

        modified[field] = value;
        modified.valid = strings.isNotBlank(modified.sourceId) && strings.isNotBlank(modified.targetId)
            && strings.isNotBlank(modified.category) && strings.isNotBlank(modified.label);

        onSync(modified);
    };

    const onChangeAs = (category, label) => {
        const modified = objects.deepCopy(data);

        modified.category = category;
        modified.label = label;
        modified.valid = strings.isNotBlank(modified.sourceId) && strings.isNotBlank(modified.targetId)
            && strings.isNotBlank(modified.category) && strings.isNotBlank(modified.label);

        onSync(modified);
    };

    return <div className="connection-form">
        {locked ?
            <Table size="small" cellPadding={0} cellSpacing={0}>
                <TableBody>
                    <TableRow>
                        <TableCell padding="none" style={{
                            paddingRight: "10px",
                            verticalAlign: "top",
                            whiteSpace: "nowrap",
                        }}>
                            <Typography variant="caption">
                                {source}
                            </Typography>
                        </TableCell>
                        <TableCell padding="none">
                            <Typography variant="caption">
                                {users.renderUser(data.sourceId, idToName)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell padding="none" style={{
                            paddingRight: "10px",
                            verticalAlign: "top",
                            whiteSpace: "nowrap",
                        }}>
                            <Typography variant="caption">
                                {target}
                            </Typography>
                        </TableCell>
                        <TableCell padding="none">
                            <Typography variant="caption">
                                {users.renderUser(data.targetId, idToName)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table> :
            <>
                <div className="field top-margin">
                    <UserField
                        required={true}
                        label={source}
                        users={sources}
                        value={data.sourceId}
                        onChange={(sourceId) => onChange("sourceId", sourceId)}
                        multiple={false}
                    />
                </div>
                <div className="field">
                    <UserField
                        required={true}
                        label={target}
                        users={targets}
                        value={data.targetId}
                        onChange={(targetId) => onChange("targetId", targetId)}
                        multiple={false}
                    />
                </div>
            </>}
        <div className="field">
            <ConnectionField
                required={true}
                label="Connected as"
                categories={categories}
                value={data.label}
                onChange={onChangeAs}
            />
        </div>
        <Typography variant="caption">
            {description}
        </Typography>
    </div>
}

export default ConnectionForm;
